import { Container, Title } from "@mantine/core";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  BackgroundOrder,
  getBackgroundOrderById,
  getBackgroundOrdersByEmail,
  getBackgroundReportHtml,
  startBackgroundOrder,
} from "../../services/background/background.service";
import PLButton from "../../components/common/button";
import PublicityBanner from "../../components/publicity-banner/publicity-banner.component";
import {
  DEMO_PUBLICITY_BANNER,
  getEnvPublicityBanner,
  isPublicityEnabled,
  resolvePublicityBanner,
} from "../../helpers/publicity.banner.helper";
import "../account/account.scss";
import "./background.page.scss";
import { useTranslation } from "react-i18next";

const READY_TO_START_STATUSES = new Set(["READY_TO_START", "READ_TO_START"]);

const normalizeStatus = (status?: string | null) => {
  return (status || "").trim().toUpperCase();
};

const canStartBackground = (order?: BackgroundOrder | null) => {
  if (!order?.paid || !order.applicants?.length) return false;
  return order.applicants.some((applicant) =>
    READY_TO_START_STATUSES.has(normalizeStatus(applicant.order_status)),
  );
};

const formatDate = (value?: string, locale: string = "en") => {
  if (!value) return "—";

  return new Date(value).toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getApplicantName = (applicant: any) => {
  return [
    applicant.first_name,
    applicant.middle_name,
    applicant.last_name,
  ]
    .filter(Boolean)
    .join(" ");
};

const formatStatusLabel = (value, t, fallback) => {
  const normalizedValue = (value || "").trim();
  if (!normalizedValue) return fallback;

  const humanized = normalizedValue
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
  return t(`background.status.${normalizedValue.toUpperCase()}`, {
    defaultValue: humanized,
  });
};

const BackgroundPage = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const userData = useSelector((state: any) => state.account.userData);
  const [backgroundOrders, setBackgroundOrders] = useState<BackgroundOrder[]>(
    [],
  );
  const [selectedBackgroundOrderId, setSelectedBackgroundOrderId] = useState<
    string | null
  >(null);
  const [loadingBackground, setLoadingBackground] = useState(false);
  const [buyingBackground, setBuyingBackground] = useState(false);
  const [startingBackgroundOrderId, setStartingBackgroundOrderId] = useState<
    string | null
  >(null);
  const [downloadingApplicantId, setDownloadingApplicantId] = useState<
    string | null
  >(null);

  const backgroundEmail = userData.account_email || userData.alternate_email;
  const userBackgroundBanner = userData?.backgroundBanner;
  const userPublicityBanner = userData?.publicityBanner;
  const userAdvertisementBanner = userData?.advertisementBanner;
  const userBannersBackground = userData?.banners?.background;
  const userBannersDefault = userData?.banners?.default;
  const backgroundBannerEnabled = isPublicityEnabled({
    area: "BACKGROUND",
    kind: "BANNER",
  });
  const backgroundPageBanner = useMemo(
    () =>
      backgroundBannerEnabled
        ? resolvePublicityBanner(
            userBackgroundBanner,
            userPublicityBanner,
            userAdvertisementBanner,
            userBannersBackground,
            userBannersDefault,
            getEnvPublicityBanner("BACKGROUND"),
            DEMO_PUBLICITY_BANNER,
          )
        : null,
    [
      backgroundBannerEnabled,
      userAdvertisementBanner,
      userBackgroundBanner,
      userBannersBackground,
      userBannersDefault,
      userPublicityBanner,
    ],
  );
  const orderIdFromQuery = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("order_id");
  }, [location.search]);

  const selectedBackgroundOrder = useMemo(() => {
    if (!backgroundOrders.length) return null;
    if (!selectedBackgroundOrderId) return backgroundOrders[0];

    return (
      backgroundOrders.find((order) => order.id === selectedBackgroundOrderId) ||
      backgroundOrders[0]
    );
  }, [backgroundOrders, selectedBackgroundOrderId]);

  const selectedApplicant = useMemo(() => {
    return selectedBackgroundOrder?.applicants?.[0] || null;
  }, [selectedBackgroundOrder]);

  const fetchBackgroundOrders = useCallback(
    async (preferredOrderId?: string) => {
      if (!backgroundEmail) {
        setBackgroundOrders([]);
        setSelectedBackgroundOrderId(null);
        return;
      }

      try {
        setLoadingBackground(true);
        const orders = await getBackgroundOrdersByEmail();
        const normalizedOrders = Array.isArray(orders) ? orders : [];
        setBackgroundOrders(normalizedOrders);

        if (!normalizedOrders.length) {
          setSelectedBackgroundOrderId(null);
          return;
        }

        const nextSelectedOrderId =
          preferredOrderId &&
          normalizedOrders.some((order) => order.id === preferredOrderId)
            ? preferredOrderId
            : selectedBackgroundOrderId &&
                normalizedOrders.some(
                  (order) => order.id === selectedBackgroundOrderId,
                )
              ? selectedBackgroundOrderId
              : normalizedOrders[0].id;

        setSelectedBackgroundOrderId(nextSelectedOrderId);
      } catch (_error) {
        toast.error(t("background.loadError"));
      } finally {
        setLoadingBackground(false);
      }
    },
    [backgroundEmail, selectedBackgroundOrderId, t],
  );

  useEffect(() => {
    fetchBackgroundOrders(orderIdFromQuery || undefined);
  }, [fetchBackgroundOrders, orderIdFromQuery]);

  const handleBuyBackgroundCheck = async () => {
    if (!backgroundEmail) {
      toast.error(t("payment.emailRequired"));
      return;
    }

    setBuyingBackground(true);
    history.push("/payment?product=background");
  };

  const handleRefreshBackground = async (orderId?: string) => {
    const targetOrderId = orderId || selectedBackgroundOrder?.id;
    if (!targetOrderId) return;

    try {
      setLoadingBackground(true);
      const order = await getBackgroundOrderById(targetOrderId);
      setBackgroundOrders((prev) => {
        const rest = prev.filter((item) => item.id !== order.id);
        return [order, ...rest];
      });
      setSelectedBackgroundOrderId(order.id);
    } catch (_error) {
      toast.error(t("background.refreshError"));
    } finally {
      setLoadingBackground(false);
    }
  };

  const handleStartBackground = async (orderId: string) => {
    try {
      setStartingBackgroundOrderId(orderId);
      setSelectedBackgroundOrderId(orderId);
      const result = await startBackgroundOrder(orderId);
      await handleRefreshBackground(orderId);
      toast.success(
        result.started_count === 1
          ? t("background.startedOne")
          : t("background.startedMany", { count: result.started_count }),
      );
    } catch (_error) {
      toast.error(t("background.startError"));
    } finally {
      setStartingBackgroundOrderId(null);
    }
  };

  const handleDownloadReport = async (applicantId: string) => {
    try {
      setDownloadingApplicantId(applicantId);
      const html = await getBackgroundReportHtml(applicantId);
      const blob = new Blob([html], { type: "text/html" });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    } catch (_error) {
      toast.error(t("background.reportError"));
    } finally {
      setDownloadingApplicantId(null);
    }
  };

  return (
    <Container size={"xl"} className="u-w-100 background-page">
      <div className="background-check-panel">
        <div className="background-check-panel__header">
          <div>
            <Title order={2}>{t("background.title")}</Title>
            <p className="background-check-panel__subtitle">
              {t("background.description")}
            </p>
          </div>
          <div className="background-check-panel__actions">
            <PLButton
              variant="secondary"
              size="md"
              onClick={() => fetchBackgroundOrders()}
              className="background-check-panel__btn"
              disabled={loadingBackground || !backgroundEmail}
            >
              {loadingBackground ? t("common.refreshing") : t("background.refreshAll")}
            </PLButton>
            <PLButton
              variant="primary"
              size="md"
              onClick={handleBuyBackgroundCheck}
              className="background-check-panel__btn"
              disabled={buyingBackground || !backgroundEmail}
            >
              {buyingBackground ? t("common.redirecting") : t("background.buy")}
            </PLButton>
          </div>
        </div>

        <PublicityBanner
          banner={backgroundPageBanner}
          className="background-check-panel__banner"
        />

        {!backgroundEmail ? (
          <p className="background-check-panel__empty">
            {t("background.emailRequired")}
          </p>
        ) : !backgroundOrders.length ? (
          <p className="background-check-panel__empty">
            {t("background.empty")}
          </p>
        ) : (
          <>
            <div className="background-check-panel__order-list">
              {backgroundOrders.map((order) => (
                <div
                  key={order.id}
                  className={`background-check-panel__order-card ${
                    selectedBackgroundOrder?.id === order.id
                      ? "background-check-panel__order-card--active"
                      : ""
                  }`}
                >
                  <div className="background-check-panel__order-card-main">
                    <div className="background-check-panel__order-meta background-check-panel__order-meta--id">
                      <span className="background-check-panel__label">
                        {t("background.orderId")}
                      </span>
                      <strong className="background-check-panel__order-id">
                        {order.id}
                      </strong>
                    </div>
                    <div className="background-check-panel__order-meta background-check-panel__order-meta--created">
                      <span className="background-check-panel__label">
                        {t("background.created")}
                      </span>
                      <strong>{formatDate(order.created_at, i18n.resolvedLanguage)}</strong>
                    </div>
                    <div className="background-check-panel__order-meta background-check-panel__order-meta--status">
                      <span className="background-check-panel__label">
                        {t("background.statusLabel")}
                      </span>
                      <strong>{t(order.paid ? "background.purchased" : "background.awaitingPayment")}</strong>
                    </div>
                    <div className="background-check-panel__order-meta background-check-panel__order-meta--applicants">
                      <span className="background-check-panel__label">
                        {t("background.applicants")}
                      </span>
                      <strong>{order.applicants?.length || 0}</strong>
                    </div>
                  </div>
                  <div className="background-check-panel__order-card-actions">
                    <PLButton
                      variant="secondary"
                      size="sm"
                      className="background-check-panel__btn"
                      onClick={() => setSelectedBackgroundOrderId(order.id)}
                    >
                      {t("background.viewDetails")}
                    </PLButton>
                    <PLButton
                      variant="secondary"
                      size="sm"
                      className="background-check-panel__btn"
                      onClick={() => handleRefreshBackground(order.id)}
                      disabled={loadingBackground}
                    >
                      {t("common.refresh")}
                    </PLButton>
                    {canStartBackground(order) ? (
                      <PLButton
                        variant="blue"
                        size="sm"
                        className="background-check-panel__btn"
                        onClick={() => handleStartBackground(order.id)}
                        disabled={
                          loadingBackground || startingBackgroundOrderId === order.id
                        }
                      >
                        {startingBackgroundOrderId === order.id
                          ? t("background.starting")
                          : t("background.start")}
                      </PLButton>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            {selectedBackgroundOrder ? (
              <>
                <div className="background-check-panel__summary">
                  <div>
                    <span className="background-check-panel__label">
                      {t("background.recipient")}
                    </span>
                    <strong>{selectedBackgroundOrder.main_recipient}</strong>
                  </div>
                  <div>
                    <span className="background-check-panel__label">
                      {t("background.payment")}
                    </span>
                    <strong>
                      {selectedBackgroundOrder.paid
                        ? t("background.purchased")
                        : t("background.awaitingPayment")}
                    </strong>
                  </div>
                  <div>
                    <span className="background-check-panel__label">
                      {t("background.updated")}
                    </span>
                    <strong>{formatDate(selectedBackgroundOrder.updated_at, i18n.resolvedLanguage)}</strong>
                  </div>
                </div>

                <div className="background-check-panel__links">
                  {selectedBackgroundOrder.invoice_url ? (
                    <a
                      href={selectedBackgroundOrder.invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="background-check-panel__link"
                    >
                      {t("background.downloadInvoice")}
                    </a>
                  ) : null}
                </div>

                {selectedApplicant ? (
                  <div className="background-check-panel__applicant-card">
                    <div className="background-check-panel__applicant-header">
                      <div>
                        <span className="background-check-panel__label">
                          {t("background.applicant")}
                        </span>
                        <h3>{getApplicantName(selectedApplicant) || t("background.applicant")}</h3>
                        <p>{selectedApplicant.email || t("background.noEmail")}</p>
                      </div>

                      {canStartBackground(selectedBackgroundOrder) ? (
                        <PLButton
                          variant="blue"
                          size="md"
                          className="background-check-panel__btn"
                          onClick={() =>
                            handleStartBackground(selectedBackgroundOrder.id)
                          }
                          disabled={
                            loadingBackground ||
                            startingBackgroundOrderId === selectedBackgroundOrder.id
                          }
                        >
                          {startingBackgroundOrderId === selectedBackgroundOrder.id
                            ? t("background.starting")
                            : t("background.start")}
                        </PLButton>
                      ) : null}
                    </div>

                    <div className="background-check-panel__applicant-grid">
                      <div className="background-check-panel__applicant-item">
                        <span>{t("background.orderStatus")}</span>
                        <strong>
                          {formatStatusLabel(selectedApplicant.order_status, t, t("common.pending"))}
                        </strong>
                      </div>
                      <div className="background-check-panel__applicant-item">
                        <span>{t("background.approval")}</span>
                        <strong>
                          {formatStatusLabel(
                            selectedApplicant.approval_status,
                            t,
                            t("common.pending"),
                          )}
                        </strong>
                      </div>
                      <div className="background-check-panel__applicant-item">
                        <span>{t("background.report")}</span>
                        <strong>
                          {t(selectedApplicant.report_available ? "common.available" : "common.notReady")}
                        </strong>
                      </div>
                    </div>

                    {selectedApplicant.report_available ? (
                      <div className="background-check-panel__applicant-actions">
                        <PLButton
                          variant="primary"
                          size="md"
                          className="background-check-panel__btn"
                          onClick={() => handleDownloadReport(selectedApplicant.id)}
                          disabled={downloadingApplicantId === selectedApplicant.id}
                        >
                          {downloadingApplicantId === selectedApplicant.id
                            ? t("common.opening")
                            : t("background.downloadReport")}
                        </PLButton>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <p className="background-check-panel__empty">
                    {t("background.notReadyDescription")}
                  </p>
                )}
              </>
            ) : null}
          </>
        )}
      </div>
    </Container>
  );
};

export default BackgroundPage;
